import { useState, useEffect } from "react";
import { useToolOpener } from "@/lib/dockBus";
import { Link } from "react-router-dom";
import { LifeBuoy, LogOut, Phone, ShieldCheck, MessageSquareWarning, MapPin, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HELPLINES = [
  { name: "Emergency (Police / Ambulance / Fire)", number: "999", tel: "999" },
  { name: "NHS non-emergency health advice", number: "111", tel: "111" },
  { name: "National Domestic Abuse Helpline (24h, free)", number: "0808 2000 247", tel: "08082000247" },
  { name: "Samaritans (emotional support, 24h)", number: "116 123", tel: "116123" },
];

export function SafetyButton() {
  const [open, setOpen] = useState(false);
  useToolOpener("safety", () => setOpen(true));
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    if (open && user) api.get("/love/trusted-contacts").then(({ data }) => setContacts(data.contacts || [])).catch(() => {});
  }, [open, user]);

  const saveContacts = async (next) => {
    setContacts(next);
    try { await api.put("/love/trusted-contacts", { contacts: next }); } catch { toast.error("Could not save contacts."); }
  };
  const addContact = () => { if (contacts.length < 5) saveContacts([...contacts, { name: "", phone: "" }]); };
  const updateContact = (i, k, v) => setContacts((c) => c.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const removeContact = (i) => saveContacts(contacts.filter((_, idx) => idx !== i));

  const shareLocation = () => {
    if (!navigator.geolocation) { toast.error("Location not available on this device."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition((pos) => {
      setLocating(false);
      const { latitude, longitude } = pos.coords;
      const mapUrl = `https://maps.google.com/?q=${latitude},${longitude}`;
      const text = `I'm sharing my live location with you for safety: ${mapUrl}`;
      if (navigator.share) {
        navigator.share({ title: "My location", text, url: mapUrl }).catch(() => {});
      } else {
        navigator.clipboard?.writeText(text);
        toast.success("Location link copied — paste it to your trusted contact.");
      }
    }, () => { setLocating(false); toast.error("Could not get your location. Please allow location access."); }, { enableHighAccuracy: true, timeout: 10000 });
  };

  const quickExit = () => {
    try { window.open("https://www.bbc.co.uk/weather", "_blank", "noopener"); } catch {}
    window.location.replace("https://www.google.com");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="safety-button"
          aria-label="Safety and quick exit"
          className="fixed right-0 top-1/2 -translate-y-1/2 z-50 flex flex-col items-center gap-1 rounded-l-xl bg-pula-accent text-white px-2.5 py-3 shadow-lg hover:bg-pula-accent/90 transition-colors">
          <LifeBuoy className="w-5 h-5" />
          <span className="text-[11px] font-semibold tracking-wide font-body [writing-mode:vertical-rl] rotate-180">Safety</span>
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="safety-panel">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><LifeBuoy className="w-5 h-5 text-pula-accent" /> Safety &amp; support</DialogTitle>
          <DialogDescription>If you feel unsafe, you can leave this site instantly or reach help below.</DialogDescription>
        </DialogHeader>

        <Button data-testid="safety-quick-exit" onClick={quickExit}
          className="w-full rounded-full bg-pula-accent hover:bg-pula-accent/90 text-white h-12 text-base">
          <LogOut className="w-5 h-5 mr-2" /> Leave this site now
        </Button>
        <p className="text-xs text-pula-inksoft font-body -mt-1">
          This opens a weather page and replaces Pula in your history. For full privacy, also clear your browser history afterwards.
        </p>

        <div className="mt-2">
          <p className="font-heading font-bold text-sm text-pula-ink flex items-center gap-2"><Phone className="w-4 h-4 text-pula-green" /> Urgent help (UK)</p>
          <div className="mt-2 space-y-2" data-testid="safety-helplines">
            {HELPLINES.map((h) => (
              <a key={h.name} href={`tel:${h.tel}`}
                className="flex items-center justify-between gap-3 pula-card p-3 hover:bg-pula-bg2 transition-colors">
                <span className="text-sm font-body text-pula-inksoft">{h.name}</span>
                <span className="font-heading font-bold text-pula-ink whitespace-nowrap">{h.number}</span>
              </a>
            ))}
          </div>
        </div>

        <div className="mt-2 grid grid-cols-1 gap-2">
          <Link to="/safety" onClick={() => setOpen(false)} data-testid="safety-guide-link"
            className="flex items-center gap-2 pula-card p-3 hover:bg-pula-bg2 transition-colors text-sm font-body text-pula-ink">
            <ShieldCheck className="w-4 h-4 text-pula-green" /> Read our Safety Guide
          </Link>
          <Link to="/safety" onClick={() => setOpen(false)} data-testid="safety-report-link"
            className="flex items-center gap-2 pula-card p-3 hover:bg-pula-bg2 transition-colors text-sm font-body text-pula-ink">
            <MessageSquareWarning className="w-4 h-4 text-pula-accent" /> Report or block someone
          </Link>
        </div>

        {user && (
          <div className="mt-2" data-testid="safety-location-section">
            <p className="font-heading font-bold text-sm text-pula-ink flex items-center gap-2"><MapPin className="w-4 h-4 text-pula-accent" /> Share my live location</p>
            <p className="text-xs text-pula-inksoft font-body mt-1">Meeting someone? Send your current location to a trusted contact in one tap.</p>
            <Button data-testid="safety-share-location" onClick={shareLocation} disabled={locating}
              className="w-full mt-2 rounded-full bg-pula-accent hover:bg-pula-accent/90 text-white">
              {locating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />} Share my location now
            </Button>
            <div className="mt-3 space-y-2" data-testid="safety-contacts">
              <p className="text-xs font-semibold text-pula-ink font-body">Trusted contacts</p>
              {contacts.map((c, i) => (
                <div key={i} className="flex gap-2" data-testid={`safety-contact-${i}`}>
                  <Input data-testid={`safety-contact-name-${i}`} value={c.name} onChange={(e) => updateContact(i, "name", e.target.value)} onBlur={() => saveContacts(contacts)} placeholder="Name" className="h-9" />
                  <Input data-testid={`safety-contact-phone-${i}`} value={c.phone} onChange={(e) => updateContact(i, "phone", e.target.value)} onBlur={() => saveContacts(contacts)} placeholder="Phone" className="h-9" />
                  <button data-testid={`safety-contact-remove-${i}`} onClick={() => removeContact(i)} className="shrink-0 text-pula-inksoft hover:text-pula-accent"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              {contacts.length < 5 && (
                <button data-testid="safety-contact-add" onClick={addContact} className="inline-flex items-center gap-1 text-sm text-pula-green font-medium"><Plus className="w-4 h-4" /> Add trusted contact</button>
              )}
            </div>
          </div>
        )}

        <p className="text-xs text-pula-inksoft font-body mt-1">
          Pula is not an emergency service. In immediate danger, always call 999.
        </p>
      </DialogContent>
    </Dialog>
  );
}
